import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { queryKeys } from '@/lib/queryClient';
import { bookingsApi, mockBookings, type BookingsParams } from '../api/bookings.api';
import { APP_CONFIG } from '@/constants/config';
import type { PaginatedResponse, Booking } from '@/types';

export function useBookings(params: BookingsParams) {
  return useQuery({
    queryKey: queryKeys.bookings.list(params as Record<string, unknown>),
    queryFn: async (): Promise<PaginatedResponse<Booking>> => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        let filtered = [...mockBookings];

        if (params.search) {
          const search = params.search.toLowerCase();
          filtered = filtered.filter(
            (b) =>
              b.bookingNumber.toLowerCase().includes(search) ||
              b.customer.name.toLowerCase().includes(search) ||
              b.customer.phone.includes(search) ||
              b.driver?.name.toLowerCase().includes(search) ||
              b.driver?.phone.includes(search)
          );
        }

        if (params.status && params.status !== 'all') {
          filtered = filtered.filter((b) => b.status === params.status);
        }

        if (params.vehicleType && params.vehicleType !== 'all') {
          filtered = filtered.filter((b) => b.vehicleType === params.vehicleType);
        }

        if (params.paymentStatus && params.paymentStatus !== 'all') {
          filtered = filtered.filter((b) => b.payment.status === params.paymentStatus);
        }

        // Sort by createdAt desc by default
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        const page = params.page || 1;
        const limit = params.limit || 10;
        const start = (page - 1) * limit;
        const paginatedData = filtered.slice(start, start + limit);

        return {
          data: paginatedData,
          pagination: {
            page,
            limit,
            total: filtered.length,
            totalPages: Math.ceil(filtered.length / limit),
          },
        };
      }
      return bookingsApi.getAll(params);
    },
    staleTime: 30 * 1000,
  });
}

export function useBooking(id: string) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['booking', id],
    queryFn: async (): Promise<Booking> => {
      // First, try to get the booking from the cached bookings list
      const cachedData = queryClient.getQueriesData<PaginatedResponse<Booking>>({ 
        queryKey: queryKeys.bookings.list({}), // Pass empty object for base query
      });
      
      // Search through all cached booking queries
      for (const [, data] of cachedData) {
        if (data?.data) {
          const booking = data.data.find((b) => b.id === id);
          if (booking) {
            return Promise.resolve(booking);
          }
        }
      }
      
      // If not found in cache, fetch from API
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return mockBookings.find((b) => b.id === id) || mockBookings[0];
      }
      return bookingsApi.getById(id);
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    enabled: !!id, // Only fetch if id is available
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await bookingsApi.cancel(id, reason);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
      toast.success('Booking cancelled successfully');
    },
  });
}

export function useReassignBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, driverId }: { id: string; driverId: string }) => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return;
      }
      await bookingsApi.reassign(id, driverId);
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
      toast.success('Booking reassigned successfully');
    },
  });
}

export function useBookingTracking(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.bookings.tracking(id),
    queryFn: async () => {
      if (APP_CONFIG.enableMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        return {
          bookingId: id,
          driverLocation: {
            lat: 20.296 + (Math.random() * 0.05 - 0.025),
            lng: 85.824 + (Math.random() * 0.05 - 0.025),
          },
          eta: Math.floor(5 + Math.random() * 20),
          distance: Math.round((1 + Math.random() * 5) * 10) / 10,
          status: 'in_transit' as const,
          updatedAt: new Date().toISOString(),
        };
      }
      return bookingsApi.getTracking(id);
    },
    enabled: !!id && enabled,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
}
